// Cache local (IndexedDB) das fichas de cadastro (ContactRegistration) — pra
// abrir a aba Cadastro e ver fichas de cliente mesmo sem internet. Guarda o
// registro inteiro retornado por GET /contact-registrations (os mesmos
// campos que a tela normal usa: status já calculado, meses, valores,
// endereço etc.) — e não só as já abertas antes: uma sincronização completa
// roda sozinha em segundo plano (ver startRegistrationsAutoSync), buscando
// TODAS as fichas da empresa, página por página, pelo mesmo endpoint que a
// listagem já usa. Além disso, toda busca ao vivo bem sucedida (lista ou
// ficha aberta) também grava no cache de passagem, então o que foi visto por
// último fica sempre atualizado, mesmo entre sincronizações completas.
//
// Só serve pra VISUALIZAÇÃO (leitura) — edição continua exigindo internet.
// Anexos (fotos/documentos) têm cache próprio, sob demanda (ver
// registrationAttachmentCache.js), porque são pesados demais pra baixar tudo
// de uma vez junto com o texto.
import api from "../services/api";
import { openLocalDb, REGISTRATIONS_STORE } from "./localDb";

// Marca (localStorage, leve) de quando a última sincronização completa
// terminou com sucesso — usada só pra decidir se vale rodar outra, nunca pra
// bloquear leitura do cache (leitura sempre serve o que tiver, mesmo velho).
const LAST_FULL_SYNC_KEY = "agilzap_registrations_full_sync_at";
const FULL_SYNC_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h

const hasStore = (db) =>
  Boolean(
    db && db.objectStoreNames && db.objectStoreNames.contains(REGISTRATIONS_STORE)
  );

const txDone = (tx) =>
  new Promise((resolve) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
    tx.onabort = () => resolve(false);
  });

export const getCachedRegistrations = async () => {
  try {
    const db = await openLocalDb();
    if (!hasStore(db)) return [];
    return await new Promise((resolve) => {
      const req = db
        .transaction(REGISTRATIONS_STORE, "readonly")
        .objectStore(REGISTRATIONS_STORE)
        .getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch (e) {
    return [];
  }
};

export const getCachedRegistrationsByContactId = async (contactId) => {
  const cid = Number(contactId || 0);
  if (!cid) return [];
  try {
    const db = await openLocalDb();
    if (!hasStore(db)) return [];
    const store = db
      .transaction(REGISTRATIONS_STORE, "readonly")
      .objectStore(REGISTRATIONS_STORE);
    if (store.indexNames && store.indexNames.contains("contactId")) {
      return await new Promise((resolve) => {
        const req = store.index("contactId").getAll(cid);
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => resolve([]);
      });
    }
    const all = await getCachedRegistrations();
    return all.filter((r) => Number(r?.contactId || 0) === cid);
  } catch (e) {
    return [];
  }
};

export const getCachedRegistrationById = async (id) => {
  const rid = Number(id || 0);
  if (!rid) return null;
  try {
    const db = await openLocalDb();
    if (!hasStore(db)) return null;
    return await new Promise((resolve) => {
      const req = db
        .transaction(REGISTRATIONS_STORE, "readonly")
        .objectStore(REGISTRATIONS_STORE)
        .get(rid);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
};

// Grava/atualiza várias fichas de uma vez. Usado tanto pela sincronização
// completa quanto de passagem (toda resposta ao vivo bem sucedida). Nunca
// remove nada aqui — uma ficha só sai do cache quando uma sincronização
// completa mais nova confirma que ela não existe mais (ver
// pruneMissingAfterFullSync), pra uma busca filtrada (que só vê uma fatia)
// nunca apagar do cache fichas que só não vieram por causa do filtro.
export const putCachedRegistrations = async (records) => {
  const list = Array.isArray(records) ? records : [records];
  const valid = list.filter((r) => r && Number(r.id || 0) > 0);
  if (!valid.length) return;
  try {
    const db = await openLocalDb();
    if (!hasStore(db)) return;
    const tx = db.transaction(REGISTRATIONS_STORE, "readwrite");
    const store = tx.objectStore(REGISTRATIONS_STORE);
    valid.forEach((r) =>
      store.put({ ...r, id: Number(r.id), _cachedAt: Date.now() })
    );
    await txDone(tx);
  } catch (e) {
    // silencioso — cache é só uma otimização, nunca deve quebrar o fluxo normal
  }
};

const pruneMissingAfterFullSync = async (keepIds) => {
  try {
    const db = await openLocalDb();
    if (!hasStore(db)) return;
    const keep = new Set(keepIds.map(Number));
    const all = await getCachedRegistrations();
    const toRemove = all.filter((r) => !keep.has(Number(r?.id || 0)));
    if (!toRemove.length) return;
    const tx = db.transaction(REGISTRATIONS_STORE, "readwrite");
    const store = tx.objectStore(REGISTRATIONS_STORE);
    toRemove.forEach((r) => store.delete(Number(r.id)));
    await txDone(tx);
  } catch (e) {}
};

export const getLastFullSyncAt = () => {
  try {
    return Number(window.localStorage.getItem(LAST_FULL_SYNC_KEY) || 0);
  } catch (e) {
    return 0;
  }
};

const markFullSyncDone = () => {
  try {
    window.localStorage.setItem(LAST_FULL_SYNC_KEY, String(Date.now()));
  } catch (e) {}
};

const isOnline = () =>
  typeof navigator === "undefined" || navigator.onLine !== false;

// Busca TODAS as páginas de uma região (capital/interior), sem nenhum outro
// filtro — o mesmo endpoint que a listagem normal usa, só que sem busca/
// status/cidade etc, pra pegar o universo completo de fichas daquela região.
// Grava cada página assim que chega, então uma queda de conexão no meio do
// caminho deixa como cache tudo que já tinha sido baixado até ali (não é
// tudo ou nada).
const syncRegion = async (region) => {
  const idsSeen = [];
  let pageNumber = 1;
  // Trava de segurança — nunca deveria chegar nem perto disso (hoje são
  // ~3.375 fichas / 20 por página = ~169 páginas no total).
  const HARD_PAGE_LIMIT = 2000;
  while (pageNumber <= HARD_PAGE_LIMIT) {
    if (!isOnline()) return { ok: false, idsSeen };
    let data;
    try {
      const resp = await api.get("/contact-registrations", {
        params: {
          pageNumber,
          region,
          excludeVisitCards: "true",
          sortBy: "daysLateDesc"
        }
      });
      data = resp.data;
    } catch (e) {
      return { ok: false, idsSeen };
    }
    const records = Array.isArray(data?.records) ? data.records : [];
    if (records.length) {
      await putCachedRegistrations(records);
      records.forEach((r) => idsSeen.push(Number(r.id)));
    }
    if (!data?.hasMore) return { ok: true, idsSeen };
    pageNumber += 1;
    // Folga pequena entre páginas — a sincronização completa faz ~170
    // requisições seguidas (3.375 fichas / 20 por página); isso é só pra não
    // martelar o servidor sem necessidade, já que não tem pressa nenhuma
    // (roda sozinha em segundo plano).
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
  return { ok: false, idsSeen };
};

let syncing = false;
// Sincronização completa: todas as fichas (capital + interior) da empresa
// logada. Só marca como concluída (pra não repetir de novo por 6h) se as
// DUAS regiões terminarem sem erro de rede — uma sincronização parcial (ex:
// caiu a conexão na metade) fica valendo com o que já baixou até ali, mas
// tenta de novo na próxima chance em vez de esperar as 6h completarem.
export const syncAllRegistrationsFull = async () => {
  if (syncing) return false;
  if (!isOnline()) return false;
  syncing = true;
  try {
    const capital = await syncRegion("capital");
    const interior = await syncRegion("interior");
    if (capital.ok && interior.ok) {
      await pruneMissingAfterFullSync([...capital.idsSeen, ...interior.idsSeen]);
      markFullSyncDone();
      return true;
    }
    return false;
  } finally {
    syncing = false;
  }
};

let autoSyncWired = false;
// Liga a sincronização automática: roda ao montar (se nunca sincronizou ou
// já faz mais de 6h), de novo toda vez que a conexão volta, e faz uma
// varredura leve a cada 30min (só faz trabalho de verdade se realmente
// estiver na hora e online — chamar isso com frequência é barato).
export const startRegistrationsAutoSync = () => {
  if (autoSyncWired) return;
  autoSyncWired = true;
  const maybeSync = () => {
    if (!isOnline()) return;
    if (Date.now() - getLastFullSyncAt() < FULL_SYNC_MIN_INTERVAL_MS) return;
    syncAllRegistrationsFull();
  };
  try {
    window.addEventListener("online", maybeSync);
  } catch (e) {}
  try {
    setInterval(maybeSync, 30 * 60 * 1000);
  } catch (e) {}
  maybeSync();
};
